import { Route } from '@angular/router';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { BlankLayoutComponent } from './layout/blank-layout/blank-layout.component';
import { Error403Component } from './pages/maintenance/error-403/error-403.component';
import { Error404Component } from './pages/maintenance/error-404/error-404.component';
import { Permission, permissionGuard } from './core/auth';
import { loginRouteGuard } from './core/auth/guards/login-route.guard';

export const appRoutes: Route[] = [
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      // Main Routes
      { path: '', redirectTo: '/links', pathMatch: 'full' },
      { path: 'links', canActivate: [permissionGuard(Permission.LINKS_VIEW)], loadComponent: () => import('./pages/links/links.component').then(m => m.LinksComponent) },
      { path: 'links/:linkId', canActivate: [permissionGuard(Permission.LINKS_VIEW)], loadComponent: () => import('./pages/links/links.component').then(m => m.LinksComponent) },
      { path: 'groups', canActivate: [permissionGuard(Permission.GROUPS_VIEW)], loadComponent: () => import('./pages/groups/groups.component').then(m => m.GroupsComponent) },
      { path: 'account', canActivate: [permissionGuard(Permission.ACCOUNT_VIEW)], loadComponent: () => import('./pages/account/account-page.component').then(m => m.AccountPageComponent) },

      // Admin Routes
      { path: 'admin', redirectTo: '/admin/attributes', pathMatch: 'full' },
      { path: 'admin/attributes', canActivate: [permissionGuard(Permission.ATTRIBUTES_VIEW)], loadComponent: () => import('./pages/admin/admin-attributes/admin-attributes.component').then(m => m.AdminAttributesComponent) },
      { path: 'admin/users', canActivate: [permissionGuard(Permission.USERS_VIEW)], loadComponent: () => import('./pages/admin/admin-users/admin-users.component').then(m => m.AdminUsersComponent) },
      {
        path: 'admin/users/:userID',
        canActivate: [permissionGuard(Permission.USERS_MANAGE_ROLES)],
        loadComponent: () =>
          import('./pages/admin/admin-manage-user-access/admin-manage-user-access.component').then(
            (m) => m.AdminManageUserAccessComponent
          ),
      },
      { path: 'admin/link-templates', canActivate: [permissionGuard(Permission.LINK_TEMPLATES_VIEW)], loadComponent: () => import('./pages/admin/admin-link-templates/admin-link-templates.component').then(m => m.AdminLinkTemplatesComponent) },
      { path: 'admin/qr-templates', canActivate: [permissionGuard(Permission.QR_TEMPLATES_VIEW)], loadComponent: () => import('./pages/admin/admin-qr-templates/admin-qr-templates.component').then(m => m.AdminQRTemplatesComponent) },
      { path: 'admin/tags', canActivate: [permissionGuard(Permission.TAGS_VIEW)], loadComponent: () => import('./pages/admin/admin-tags/admin-tags.component').then(m => m.AdminTagsComponent) },
      { path: 'admin/pending-links', canActivate: [permissionGuard(Permission.PENDING_LINKS_VIEW)], loadComponent: () => import('./pages/admin/admin-pending-links/admin-pending-links.component').then(m => m.AdminPendingLinksComponent) },
      { path: 'admin/trusted-destinations', canActivate: [permissionGuard(Permission.TRUSTED_DESTINATIONS_VIEW)], loadComponent: () => import('./pages/admin/admin-trusted-destinations/admin-trusted-destinations.component').then(m => m.AdminTrustedDestinationsComponent) },
      { path: 'admin/ip-exclusions', canActivate: [permissionGuard(Permission.IP_EXCLUSIONS_VIEW)], loadComponent: () => import('./pages/admin/admin-ip-exclusions/admin-ip-exclusions.component').then(m => m.AdminIpExclusionsComponent) },
      { path: 'admin/domains', canActivate: [permissionGuard(Permission.DOMAIN_VIEW)], loadComponent: () => import('./pages/admin/admin-domains/admin-domains.component').then(m => m.AdminDomainsComponent) },

      // E-Link Routes
      { path: 'admin/e-link', canActivate: [permissionGuard(Permission.E_LINKS_VIEW)], loadComponent: () => import('./pages/elink/elink.component').then(m => m.ELinkComponent) },
      { path: 'admin/e-link/add', canActivate: [permissionGuard(Permission.E_LINKS_VIEW)], loadComponent: () => import('./pages/elink/elink.component').then(m => m.ELinkComponent) },
      { path: 'admin/e-link/:uuid/edit', canActivate: [permissionGuard(Permission.E_LINKS_VIEW)], loadComponent: () => import('./pages/elink/elink.component').then(m => m.ELinkComponent) },

      // Power Pages Routes
      { path: 'power-pages', canActivate: [permissionGuard(Permission.POWER_PAGES_VIEW)], loadComponent: () => import('./pages/power-pages/power-pages.component').then(m => m.PowerPagesComponent) },
      { path: 'power-pages/add', canActivate: [permissionGuard(Permission.POWER_PAGES_VIEW)], loadComponent: () => import('./pages/power-pages/power-pages.component').then(m => m.PowerPagesComponent) },
      { path: 'power-pages/:uuid/edit', canActivate: [permissionGuard(Permission.POWER_PAGES_VIEW)], loadComponent: () => import('./pages/power-pages/power-pages.component').then(m => m.PowerPagesComponent) },

      { path: 'users', loadComponent: () => import('./pages/users/users.component').then(m => m.UsersComponent) },
    ],
  },
  // Public external E-Link routes. These render OUTSIDE MainLayoutComponent with
  // no permission guard so an SSO-authenticated, non-Blink user can view and submit
  // a (possibly SSO-protected) e-link page. Mirrors React's ELinkRoutes. Must be
  // declared before the ** wildcard so /callback is not swallowed by the redirect.
  {
    path: 'e-link/:linkSlug',
    loadComponent: () =>
      import('./pages/react-wrapper/react-wrapper.component').then(
        (m) => m.ReactWrapperComponent
      ),
  },
  {
    path: 'callback',
    loadComponent: () =>
      import('./pages/elink/elink-callback/elink-callback.component').then(
        (m) => m.ELinkCallbackComponent
      ),
  },
  {
    path: 'maintenance/403',
    component: Error403Component,
  },
  {
    path: 'maintenance/404',
    component: Error404Component,
  },
  {
    path: 'auth',
    component: BlankLayoutComponent,
    children: [
      { path: 'login', loadComponent: () => import('./pages/auth/login/login.component').then(m => m.LoginComponent) },
      { path: 'login-blink', loadComponent: () => import('./pages/auth/login/login.component').then(m => m.LoginComponent) },
    ],
  },
  {
    // Top-level login paths, as the React router had them; see loginRouteGuard.
    path: '',
    component: BlankLayoutComponent,
    children: [
      { path: 'login', canActivate: [loginRouteGuard], loadComponent: () => import('./pages/auth/login/login.component').then(m => m.LoginComponent) },
      { path: 'login-blink', canActivate: [loginRouteGuard], loadComponent: () => import('./pages/auth/login/login.component').then(m => m.LoginComponent) },
    ],
  },
  { path: '**', redirectTo: '/links' },
];
